import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { QUERY_KEYS, STALE_TIME } from '@/libs/constants'
import type { GlobalQualification, LocalQualification, Qualification } from '@/libs/interfaces'
import { qualificationsApi } from '@/services/api/qualifications'

export function useQualificationsList(params: { orgID: string; query?: string }, enabled = true) {
  return useQuery<Qualification>({
    queryKey: QUERY_KEYS.qualifications.list(params),
    queryFn: () => qualificationsApi.list(params.orgID, params.query ?? ''),
    enabled: enabled && !!params.orgID,
    staleTime: STALE_TIME['24Hours'],
  })
}

export function useQualificationsGlobalByID(
  params: { orgID: string; globalQualID: string },
  enabled = true,
) {
  return useQuery<GlobalQualification>({
    queryKey: QUERY_KEYS.qualifications.globalID(params.orgID, params.globalQualID),
    queryFn: () => qualificationsApi.getByIDGlobal(params.orgID, params.globalQualID),
    enabled: enabled && !!params.orgID && !!params.globalQualID,
    staleTime: STALE_TIME['1Hour'],
  })
}

export function useQualificationsLocalByID(
  params: { storeID: string; localQualID: string },
  enabled = true,
) {
  return useQuery<LocalQualification>({
    queryKey: QUERY_KEYS.qualifications.localID(params.storeID, params.localQualID),
    queryFn: () => qualificationsApi.getByIDLocal(params.storeID, params.localQualID),
    enabled: enabled && !!params.storeID && !!params.localQualID,
    staleTime: STALE_TIME['1Hour'],
  })
}

export function useQualificationsUpsertGlobal() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: qualificationsApi.upsertGlobalQualification,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.qualifications.all })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

export function useQualificationsUpsertLocal() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: qualificationsApi.upsertLocalQualification,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.qualifications.all })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

export function useQualificationsDeleteGlobal() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: qualificationsApi.deleteGlobalQualification,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.qualifications.all })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

export function useQualificationsDeleteLocal() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: qualificationsApi.deleteLocalQualification,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.qualifications.all })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}
